import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { siteConfig } from '@/constants/site'

export const AboutProcess = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        const media = select('[data-process-media]')[0]

        if (media) {
            gsap.fromTo(
                media,
                { clipPath: 'inset(0% 100% 0% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.5,
                    ease: 'nuvea',
                    scrollTrigger: { trigger: media, start: 'top 84%' }
                }
            )

            gsap.fromTo(
                media.querySelector('img'),
                { scale: 1.2, xPercent: -4 },
                {
                    scale: 1.08,
                    xPercent: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: media,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.8
                    }
                }
            )
        }

        gsap.from(select('[data-process-title] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1.05,
            stagger: 0.06,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 74%' }
        })

        gsap.from(select('[data-process-copy] > *'), {
            y: 26,
            opacity: 0,
            duration: 0.9,
            stagger: 0.1,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 70%' }
        })
    })

    return (
        <section
            ref={scope}
            id='process'
            className='shell-x mx-auto w-full max-w-[1760px] py-24 md:py-32'
        >
            <div className='grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16'>
                <div
                    data-process-media=''
                    className='relative aspect-[7/6] w-full overflow-hidden rounded-2xl bg-shell'
                >
                    <Image
                        src='/images/process-lab.png'
                        alt='Gloved hands lifting the finished formula from its dish with a steel spatula'
                        fill
                        sizes='(min-width: 1024px) 50vw, 100vw'
                        className='object-cover will-change-transform'
                    />
                </div>

                <div>
                    <SplitWords
                        data-process-title=''
                        as='h2'
                        lines={['How it is made']}
                        className='type-h1 text-ink'
                    />

                    <div data-process-copy='' className='mt-8 flex flex-col gap-6'>
                        <p className='type-body max-w-[52ch] text-slate-soft'>
                            Raw actives come from four suppliers in {siteConfig.madeIn}, Japan and
                            France, each one audited in person. They are blended cold at our facility
                            outside Basel, because heat is what quietly degrades ceramides and peptides
                            long before a jar ever reaches you.
                        </p>

                        <p className='type-body max-w-[52ch] text-slate-soft'>
                            Every batch is held for three separate checks: actives assay, stability, and a skin-contact
                            panel. That takes seventeen days rather than the usual four. It
                            is the least interesting part of the company and the part we are
                            least willing to shorten.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
